import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import { legalAPI } from "../services/api";
import LanguageSelect from "../components/LanguageSelect";
import terms from "../legal/terms";
import privacy from "../legal/privacy";

const DOCUMENTS = { terms, privacy };

/** `**bold**` is the only markup the texts use. */
const inline = (text) =>
  String(text)
    .split(/(\*\*[^*]+\*\*)/g)
    .map((part, index) =>
      part.startsWith("**") ? <strong key={index}>{part.slice(2, -2)}</strong> : part,
    );

/** Paragraphs, with consecutive "- " entries gathered into one list. */
const renderBody = (body) => {
  const blocks = [];

  body.forEach((entry) => {
    if (entry.startsWith("- ")) {
      const last = blocks[blocks.length - 1];
      if (last?.list) last.items.push(entry.slice(2));
      else blocks.push({ list: true, items: [entry.slice(2)] });
    } else {
      blocks.push({ text: entry });
    }
  });

  return blocks.map((block, index) =>
    block.list ? (
      <ul key={index} className="mt-2 list-disc space-y-1 ps-6 text-gray-700 leading-relaxed">
        {block.items.map((item) => (
          <li key={item}>{inline(item)}</li>
        ))}
      </ul>
    ) : (
      <p key={index} className="mt-2 text-gray-700 leading-relaxed">
        {inline(block.text)}
      </p>
    ),
  );
};

/**
 * The terms of service (`kind="terms"`) and privacy policy (`kind="privacy"`),
 * public so they can be read before an account exists. The texts live in
 * src/legal/, in all three languages. At the top, the operator's company
 * details as the platform keeps them for receipts (shown only once set).
 */
const LegalPage = ({ kind }) => {
  const { t, lang } = useI18n();
  const [operator, setOperator] = useState(null);

  useEffect(() => {
    legalAPI
      .operator()
      .then(({ data }) => setOperator(data))
      .catch(() => setOperator(null));
  }, []);

  const document = DOCUMENTS[kind][lang] || DOCUMENTS[kind].en;
  const details = operator
    ? [
        [t("legal.operator.name"), operator.company_name],
        [t("legal.operator.address"), operator.company_address],
        [t("legal.operator.taxId"), operator.company_tax_id],
        [t("legal.operator.phone"), operator.company_phone],
        [t("legal.operator.whatsapp"), operator.whatsapp_number],
      ].filter(([, value]) => value)
    : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <Link to="/login" className="text-sm text-primary-600 hover:underline">
            ← {t("auth.legal.back")}
          </Link>
          <LanguageSelect />
        </div>

        <h1 className="text-3xl font-bold text-gray-800">{document.title}</h1>
        <p className="mt-1 text-sm text-gray-500">{document.updated}</p>

        {details.length > 0 && (
          <dl className="mt-5 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm">
            {details.map(([label, value]) => (
              <div key={label} className="flex flex-wrap gap-x-2 py-0.5">
                <dt className="font-medium text-gray-600">{label}</dt>
                <dd className="text-gray-800" dir="auto">{value}</dd>
              </div>
            ))}
          </dl>
        )}

        <aside className="mt-5 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
          <h2 className="font-semibold text-gray-800">{document.summary.title}</h2>
          <ul className="mt-2 list-disc space-y-1 ps-5 text-sm text-gray-700">
            {document.summary.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </aside>

        <nav aria-label={document.title} className="mt-6">
          <ol className="space-y-1 text-sm">
            {document.sections.map((section, index) => (
              <li key={section.title}>
                <a href={`#${kind}-${index + 1}`} className="text-primary-700 hover:underline">
                  {section.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="mt-8 space-y-8">
          {document.sections.map((section, index) => (
            <section key={section.title} id={`${kind}-${index + 1}`} className="scroll-mt-4">
              <h2 className="text-lg font-semibold text-gray-800">{section.title}</h2>
              {renderBody(section.body)}
            </section>
          ))}
        </div>

        <p className="mt-10 text-sm text-gray-500 space-x-3 rtl:space-x-reverse">
          <Link to="/terms" className="hover:underline">
            {t("auth.legal.termsLink")}
          </Link>
          <Link to="/privacy" className="hover:underline">
            {t("auth.legal.privacyLink")}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LegalPage;
